'use client';
/**
 * components/auth/ConnexionTelephone.tsx — entrer avec son numéro, sans adresse.
 *
 * DEUX ÉCRANS, PAS SIX CHAMPS. On demande le numéro, on envoie un code, on le
 * saisit. Pas de nom, pas de mot de passe, pas de confirmation : chaque champ
 * supplémentaire sur un écran de six pouces est une occasion d'abandonner, et
 * tout ce qu'on demanderait ici sera de toute façon redemandé à l'endroit où
 * ça sert (le profil, le contrat).
 *
 * LE MÊME BOUTON POUR S'INSCRIRE ET SE CONNECTER. Un numéro vérifié qui n'a pas
 * de compte en ouvre un (`signUpOnVerification`), un numéro connu retrouve le
 * sien. L'utilisateur n'a pas à savoir dans quel cas il est — et se tromper
 * d'onglet est l'échec le plus banal de ces écrans.
 */
import { useState } from 'react';

import { authClient } from '@/lib/auth/client';
import { normaliserTelephone, masquerTelephone, COOKIE_ROLE } from '@/lib/auth/telephone';
import { OtpInput } from '@/components/ui/otp-input';

type Etape = { nom: 'numero' } | { nom: 'code'; numero: string };

const CHAMP =
  'champ-sombre w-full rounded-2xl bg-white/[.05] border border-white/12 px-4 py-3.5 '
  + 'text-base leading-normal text-white placeholder:text-white/35 outline-none '
  + 'focus:border-emerald-500/60 focus:bg-white/[.07] transition-colors';

const BOUTON =
  'w-full bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white '
  // clamp() plutôt qu'un padding fixe : le bouton se resserre sur les petits
  // écrans au lieu de forcer un scroll (21/09, écran de connexion trop haut
  // une fois les quatre blocs affichés en même temps).
  + 'font-black py-[clamp(0.6rem,2svh,1rem)] rounded-2xl text-base transition-colors';

function messageErreur(e: unknown, defaut: string): string {
  const err = (e as { error?: { code?: string; message?: string } })?.error;
  switch (err?.code) {
    case 'INVALID_PHONE_NUMBER':
      return 'Ce numéro ne semble pas valide.';
    case 'OTP_EXPIRED':
      return 'Le code a expiré. Demande-en un nouveau.';
    case 'INVALID_OTP':
      return 'Code incorrect.';
    default:
      return err?.message ?? (e instanceof Error ? e.message : defaut);
  }
}

export function ConnexionTelephone({ role }: { role: 'client' | 'prestataire' }) {
  const [etape, setEtape] = useState<Etape>({ nom: 'numero' });
  const [saisie, setSaisie] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const envoyerCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy) return;
    const numero = normaliserTelephone(saisie);
    if (!numero) { setErr('Entre un numéro de mobile, par exemple 06 12 34 56 78.'); return; }
    setBusy(true); setErr(null);
    try {
      /* LE RÔLE VOYAGE DANS UN TÉMOIN, pour la même raison qu'il voyage dans
         l'adresse de retour chez Google : Better Auth crée le compte lui-même à
         la vérification, sans rien savoir de cet écran. Sans ce témoin, un
         futur prestataire atterrit dans l'espace particulier (27/07). */
      document.cookie = `${COOKIE_ROLE}=${role}; path=/; max-age=900; samesite=lax`;
      const { error } = await authClient.phoneNumber.sendOtp({ phoneNumber: numero });
      if (error) throw { error };
      setEtape({ nom: 'code', numero });
      setCode('');
    } catch (e2) {
      setErr(messageErreur(e2, 'Envoi impossible — réessaie.'));
    }
    setBusy(false);
  };

  const verifier = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy || etape.nom !== 'code') return;
    setBusy(true); setErr(null);
    try {
      const { error } = await authClient.phoneNumber.verify({
        phoneNumber: etape.numero,
        code,
        // Le numéro sert d'identifiant : la session s'ouvre dans la foulée,
        // sinon il faudrait le ressaisir juste après l'avoir prouvé.
        disableSession: false,
      });
      if (error) throw { error };
      // Rechargement complet : le reste de l'application lit la session au
      // rendu serveur, un simple changement d'état la laisserait déconnectée.
      // Le rôle du compte existant fait foi après une connexion, quel que soit
      // le bouton choisi avant la saisie du numéro.
      window.location.href = '/bienvenue?auth=1';
    } catch (e2) {
      setErr(messageErreur(e2, 'Vérification impossible — réessaie.'));
      setBusy(false);
    }
  };

  if (etape.nom === 'numero') {
    return (
      <form onSubmit={envoyerCode} className="space-y-[clamp(0.5rem,2svh,0.75rem)]">
        <input className={CHAMP} type="tel" autoComplete="tel" required
               aria-label="Numéro de téléphone"
               inputMode="tel" autoCorrect="off" spellCheck={false}
               placeholder="06 12 34 56 78" value={saisie}
               onChange={ev => setSaisie(ev.target.value)} />
        {err && <p role="alert" className="text-sm text-rose-300 px-1">{err}</p>}
        <button type="submit" disabled={busy} className={BOUTON}>
          {busy ? 'Envoi…' : 'Recevoir un code par SMS'}
        </button>
        <p className="text-xs text-white/40 px-1 leading-snug">
          Connexion ou création de compte, sans mot de passe.
        </p>
      </form>
    );
  }

  return (
    <form onSubmit={verifier} className="space-y-3">
      <p className="text-sm text-white/60 px-1">
        Code envoyé au {masquerTelephone(etape.numero)}.
      </p>
      <div className="flex justify-center py-1">
        <OtpInput length={6} value={code} onChange={setCode} autoFocus />
      </div>
      {err && <p role="alert" className="text-sm text-rose-300 px-1">{err}</p>}
      <button type="submit" disabled={busy || code.length < 6} className={BOUTON}>
        {busy ? 'Vérification…' : 'Continuer'}
      </button>
      <button type="button" disabled={busy}
              onClick={() => { setEtape({ nom: 'numero' }); setErr(null); }}
              className="w-full text-sm text-white/50 hover:text-white/80 py-2 transition-colors">
        Changer de numéro
      </button>
    </form>
  );
}
